import { authService } from '@/features/auth/services/auth.service'
import { useAuthStore } from '@/store/auth-store'

/**
 * Promise de refresh em andamento, compartilhada por todas as requisições que
 * receberam 401 simultaneamente (single-flight — RF05). `null` quando nenhum
 * refresh está em andamento.
 */
let refreshPromise: Promise<string> | null = null

/**
 * Sessão irrecuperável: limpa o estado local imediatamente (garantido mesmo
 * sem nenhum listener montado) e dispara `navega:session-expired` para que o
 * `ProtectedRoute` (ou qualquer componente montado) redirecione para
 * `/login` — o evento continua existindo, mas agora só representa a falha
 * definitiva após a tentativa de refresh, nunca mais o próprio 401 inicial.
 */
function handleUnrecoverableSession(): void {
  useAuthStore.getState().clearAuth()
  window.dispatchEvent(new CustomEvent('navega:session-expired'))
}

async function performRefresh(): Promise<string> {
  const { refreshToken } = useAuthStore.getState()

  if (!refreshToken) {
    handleUnrecoverableSession()
    throw new Error('Não há refresh token disponível para renovar a sessão.')
  }

  try {
    const session = await authService.refresh({ refreshToken })
    // A API rotaciona o refresh token a cada uso — o par (novo access +
    // novo refresh) precisa ser persistido por completo; nunca reaproveitar
    // o refreshToken anterior.
    useAuthStore
      .getState()
      .setTokens(session.accessToken, session.refreshToken, session.mustChangePassword)
    return session.accessToken
  } catch (error) {
    // 401 do próprio endpoint de refresh é tratado como fatal — inclui o
    // caso de detecção de reuso, onde a API já revogou todas as sessões.
    // Não há nova tentativa de refresh.
    handleUnrecoverableSession()
    throw error
  }
}

/**
 * Garante que, mesmo com múltiplas requisições recebendo 401 ao mesmo tempo,
 * apenas uma chamada a `POST /admin/auth/refresh` seja disparada. Todas as
 * chamadas concorrentes aguardam a mesma promise em andamento.
 */
export function getRefreshedAccessToken(): Promise<string> {
  refreshPromise ??= performRefresh().finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}
